const getFormatedDateToInputFormat = (initialDate, finalDate) => {
  if (!initialDate || !finalDate) return
  const arrayInitialDate = initialDate.split(/(?:-|T|:)+/)
  const arrayFinalDate = finalDate.split(/(?:-|T|:)+/)
  const de = `${arrayInitialDate[2]}/${arrayInitialDate[1]}/${arrayInitialDate[0]}`
  const ate = `${arrayFinalDate[2]}/${arrayFinalDate[1]}/${arrayFinalDate[0]}`
  const horaDe = `${arrayInitialDate[3]}:${arrayInitialDate[4]}`
  const horaAte = `${arrayFinalDate[3]}:${arrayFinalDate[4]}`
  // eslint-disable-next-line consistent-return
  return { de, ate, horaDe, horaAte }
}

const getPastDateInTimestamp = (pastDays, endOfDay = false) => {
  if (Number(pastDays)) {
    const currentDate = new Date()
    const startDate = new Date(currentDate.setDate(currentDate.getDate() - pastDays))

    if (endOfDay) {
      startDate.setHours(23, 59, 59)
    } else {
      startDate.setHours(0, 0, 0, 0)
    }

    return startDate.getTime() / 1000
  }

  return null
}

/**
 * Função auxiliar para extrair valores de partes específicas do formato de data/hora
 * @param {Array} parts - Array de partes formatadas
 * @param {string} type - Tipo da parte a ser extraída (ex: 'year', 'month', 'fractionalSecond')
 * @param {number} length - Tamanho desejado do preenchimento com zeros (padrão: 2)
 */
function getPartValue(parts, type, length = 2) {
  const part = parts.find((partItem) => partItem.type === type)
  const defaultValue = '0'.repeat(length)

  return part ? part.value.padStart(length, '0') : defaultValue
}

/**
 *
 * @param {Date} date
 * @typedef {Object} FormatedDate
 * @property {string} utcWithTimezone Full ISO string with timezone offset
 * @property {string} utcString Standard UTC ISO string
 * @property {string} timezoneOffset Just the timezone offset
 * @property {string} clientWithTimezone Full client string with timezone offset
 * @property {string} clientTimeString Client time string in 'HH:mm:ss' format
 * @property {string} clientWithoutTimezone Client datetime string without timezone offset in 'YYYY-MM-DDThh:mm:ss' format
 * @property {string} clientDateString Client date string in 'YYYY-MM-DD' format
 * @property {string} clientZuluDateTime Client date string in 'YYYY-MM-DDTHH:mm:ss.XXXZ' format
 * @returns {FormatedDate}
 */
const getDateWithClientTimezone = (date) => {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    throw new Error('Invalid date object provided')
  }

  try {
    const utcString = date.toISOString()

    const timezoneOffsetMinutes = date.getTimezoneOffset()
    const absOffsetMinutes = Math.abs(timezoneOffsetMinutes)
    const offsetHours = Math.floor(absOffsetMinutes / 60)
    const offsetMinutes = absOffsetMinutes % 60
    const offsetSign = timezoneOffsetMinutes <= 0 ? '+' : '-'
    const timezoneString = `${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes
      .toString()
      .padStart(2, '0')}`

    const utcWithTimezone = utcString.replace('Z', '') + timezoneString

    // Use 'en-CA' to ensure 2-digit month and day on format 'YYYY-MM-DD'
    const dateFormatter = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })

    // Use 'en-GB' to ensure 24-hour on format 'HH:mm:ss'
    // Note: hour12: false is not supported in all browsers
    const timeFormatter = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
      hour12: false,
    })

    const dateParts = dateFormatter.formatToParts(date)
    const timeParts = timeFormatter.formatToParts(date)

    const clientYear = getPartValue(dateParts, 'year')
    const clientMonth = getPartValue(dateParts, 'month')
    const clientDay = getPartValue(dateParts, 'day')

    const clientHour = getPartValue(timeParts, 'hour')
    const clientMinute = getPartValue(timeParts, 'minute')
    const clientSecond = getPartValue(timeParts, 'second')
    const clientMillisecond = getPartValue(timeParts, 'fractionalSecond', 3)

    const clientDateString = `${clientYear}-${clientMonth}-${clientDay}`
    const clientTimeString = `${clientHour}:${clientMinute}:${clientSecond}`
    const clientWithTimezone = `${clientDateString}T${clientTimeString}${timezoneString}`
    const clientWithoutTimezone = `${clientYear}-${clientMonth}-${clientDay}T${clientTimeString}`
    const clientZuluDateTime = `${clientDateString}T${clientTimeString}.${clientMillisecond}Z`

    return {
      utcWithTimezone,
      utcString,
      timezoneOffset: timezoneString,
      clientWithTimezone,
      clientWithoutTimezone,
      clientTimeString,
      clientDateString,
      clientZuluDateTime,
    }
  } catch (error) {
    console.error('Error formatting date with timezone:', error)
    return null
  }
}

export { getFormatedDateToInputFormat, getPastDateInTimestamp, getDateWithClientTimezone }
